import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Button from "../../components/Button";
import Input from "../../features/forms/Input";
import ChipSelector from "../../features/forms/ChipSelector";
import DropdownSelector from "../../features/forms/DropdownSelector";
import Card from "../_components/Card";
import Stepper from "../_components/Stepper";
import styles from "./onboardingPage.module.css";

const textbooksByGrade = {
  5: [
    { id: "horizontai_5", series: "Horizontai", publisher: "Šviesa" },
  ],
  6: [
    { id: "horizontai_6", series: "Horizontai", publisher: "Šviesa" },
  ],
  7: [
    { id: "horizontai_7", series: "Horizontai", publisher: "Šviesa" },
    { id: "tau_7", series: "Tau", publisher: "TEV" },
    { id: "visiems_7", series: "Visiems", publisher: "TEV" },
  ],
  8: [
    { id: "horizontai_8", series: "Horizontai", publisher: "Šviesa" },
    { id: "tau_8", series: "Tau", publisher: "TEV" },
    { id: "visiems_8", series: "Visiems", publisher: "TEV" },
  ],
  9: [
    { id: "horizontai_9", series: "Horizontai", publisher: "Šviesa" },
    { id: "visiems_9", series: "Visiems", publisher: "TEV" },
  ],
  10: [
    { id: "horizontai_10", series: "Horizontai", publisher: "Šviesa" },
    { id: "tempus_10", series: "Tempus", publisher: "TEV" },
  ],
  11: [
    { id: "horizontai_11_bk", series: "Horizontai (bendrasis)", publisher: "Šviesa" },
    { id: "horizontai_11_isk", series: "Horizontai (išplėstinis)", publisher: "Šviesa" },
    { id: "tempus_11_isk", series: "Tempus (išplėstinis)", publisher: "TEV" },
  ],
  12: [
    { id: "horizontai_12_isk", series: "Horizontai (išplėstinis)", publisher: "Šviesa" },
    { id: "tempus_12_isk", series: "Tempus (išplėstinis)", publisher: "TEV" },
  ],
};

const getTextbooksForGrade = (gradeId, t) => {
  if (!gradeId) return [];
  const books = (textbooksByGrade[gradeId] || []).map((tb) => ({
    id: tb.id,
    label: `Matematika ${tb.series}`,
    subtitle: tb.publisher,
    cover: `/mockups/textbooks/${tb.id}.png`,
  }));
  books.push({
    id: "other",
    label: t("onboarding.textbookOther"),
    subtitle: "",
    cover: null,
  });
  return books;
};

const StepAbout = ({ data, onChange, t }) => (
  <div className={styles.stepContent}>
    <div className={styles.field}>
      <label className={styles.fieldLabel}>{t("onboarding.nameQuestion")}</label>
      <p className={styles.fieldSubtitle}>{t("onboarding.nameHint")}</p>
      <Input
        name="firstName"
        placeholder={t("onboarding.namePlaceholder")}
        value={data.firstName}
        onChange={(e) => onChange("firstName", e.target.value)}
        noLabel
      />
    </div>
  </div>
);

const StepSchool = ({ data, onChange, t }) => {
  const gradeOptions = [
    { id: "5", label: t("onboarding.grade5") },
    { id: "6", label: t("onboarding.grade6") },
    { id: "7", label: t("onboarding.grade7") },
    { id: "8", label: t("onboarding.grade8") },
    { id: "9", label: t("onboarding.grade9") },
    { id: "10", label: t("onboarding.grade10") },
    { id: "11", label: t("onboarding.grade11") },
    { id: "12", label: t("onboarding.grade12") },
  ];

  const availableTextbooks = getTextbooksForGrade(data.grade?.id, t);

  const handleGradeChange = (option) => {
    onChange("grade", option);
    onChange("textbook", null);
  };

  return (
    <div className={styles.stepContent}>
      <div className={styles.field}>
        <DropdownSelector
          options={gradeOptions}
          value={data.grade}
          defaultValue={{ id: null, label: t("onboarding.gradePlaceholder") }}
          onChange={handleGradeChange}
        />
      </div>
      {data.grade?.id && (
        <div className={styles.field}>
          <label className={styles.fieldLabel}>
            {t("onboarding.textbook")}
          </label>
          <p className={styles.fieldSubtitle}>
            {t("onboarding.textbookSubtitle")}
          </p>
          <div className={styles.textbookGrid}>
            {availableTextbooks.map((book) => (
              <div
                key={book.id}
                className={`${styles.textbookCard} ${
                  data.textbook?.id === book.id ? styles.textbookSelected : ""
                }`}
                onClick={() => onChange("textbook", book)}
              >
                {book.cover ? (
                  <img
                    src={book.cover}
                    alt={book.label}
                    className={styles.textbookCover}
                  />
                ) : (
                  <div className={styles.textbookPlaceholder}>?</div>
                )}
                <span className={styles.textbookLabel}>{book.label}</span>
                <span className={styles.textbookPublisher}>{book.subtitle}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const StepGoals = ({ data, onChange, t }) => {
  const goalOptions = [
    { id: "exam", label: t("onboarding.goalExam") },
    { id: "test", label: t("onboarding.goalTest") },
    { id: "gaps", label: t("onboarding.goalGaps") },
    { id: "ahead", label: t("onboarding.goalAhead") },
    { id: "homework", label: t("onboarding.goalHomework") },
    { id: "olympiad", label: t("onboarding.goalOlympiad") },
  ];

  const handleToggle = (option) => {
    const current = data.goals || [];
    const exists = current.find((g) => g.id === option.id);
    if (exists) {
      onChange(
        "goals",
        current.filter((g) => g.id !== option.id)
      );
    } else {
      onChange("goals", [...current, option]);
    }
  };

  return (
    <div className={styles.stepContent}>
      <p className={styles.fieldSubtitle}>{t("onboarding.goalsSubtitle")}</p>
      <ChipSelector
        options={goalOptions}
        value={data.goals || []}
        onChange={handleToggle}
      />
    </div>
  );
};

const OnboardingPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [data, setData] = useState({
    firstName: "",
    grade: null,
    textbook: null,
    goals: [],
  });

  const steps = [
    t("onboarding.stepAbout"),
    t("onboarding.stepSchool"),
    t("onboarding.stepGoals"),
  ];

  const handleChange = (field, value) => {
    setData((prev) => ({ ...prev, [field]: value }));
  };

  const stepComponents = [
    <StepAbout data={data} onChange={handleChange} t={t} />,
    <StepSchool data={data} onChange={handleChange} t={t} />,
    <StepGoals data={data} onChange={handleChange} t={t} />,
  ];

  return (
    <div className={styles.page}>
      <Card>
        <div className={styles.header}>
          <img
            src="/logotype.png"
            alt="Averbum"
            className={styles.logo}
          />
          <h2 className={styles.title}>{t("onboarding.welcome")}</h2>
          <p className={styles.subtitle}>{t("onboarding.welcomeSubtitle")}</p>
        </div>

        <Stepper steps={steps} currentStep={step} />

        <h3 className={styles.stepTitle}>{steps[step]}</h3>

        {stepComponents[step]}

        <div className={styles.actions}>
          {step > 0 && (
            <Button onClick={() => setStep(step - 1)}>
              {t("onboarding.back")}
            </Button>
          )}
          <div className={styles.spacer} />
          {step < steps.length - 1 ? (
            <Button important onClick={() => setStep(step + 1)}>
              {t("onboarding.next")}
            </Button>
          ) : (
            <Button important onClick={() => navigate("/dashboard")}>
              {t("onboarding.finish")}
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
};

export default OnboardingPage;
